import React, {
  useState,
  useCallback,
  useReducer,
  useEffect,
  useRef,
} from "react";
import moment from "moment";
import Joi from "joi";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  ButtonBase,
  Tab,
  Tabs,
  makeStyles,
  InputAdornment,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  showError,
  showSuccess,
  GetParams,
  getErrorByName,
} from "../../../../../../../../Helper";
import {
  Spinner,
  Inputs,
  DatePickerComponent,
  AutocompleteComponent,
  SelectComponet,
  MultiValueTextInput,
} from "../../../../../../../../Components";
import {
  CreatePropertyCampaign,
  UpdatePropertyCampaign,
  lookupItemsGetId,
  lookupItemsGet,
  GetAllBranches,
  GetLookupItemsByLookupTypeName,
  GetAllConvoloProjects,
} from "../../../../../../../../Services";
import { ApplicationUserSearch } from "../../../../../../../../Services/userServices";
import { StaticLookupsIds } from "../../../../../../../../assets/json/StaticLookupsIds";
import { config } from "../../../../../../../../config";

export const CampaignManangementDialog = ({
  parentTranslationPath,
  activeItem,
  isDialogOpen,
  onClose,
  onSave,
  developerId,
}) => {
  const { t } = useTranslation(parentTranslationPath);
  const searchTimer = useRef(null);
  const useStyles = makeStyles((theme) => ({
    root: {
      padding: "0px",
      paddingBottom: "5px",
      textTransform: "initial",
    },
    indicator: {
      backgroundColor: "var(--c-primary)",
    },
    labelIcon: {
      minHeight: "unset",
    },
    wrapper: {
      display: "flex",
      alignItems: "flex-start",
      flexDirection: "row",
    },
    fixed: {
      width: "fit-content",
      margin: "auto",
    },
    scroller: {
      flex: "unset",
    },
  }));
  const classes = useStyles();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const reducer = useCallback((state, action) => {
    if (action.id !== "edit") return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);

  const [state, setState] = useReducer(reducer, {
    propertyId: !developerId ? +GetParams("id") : undefined,
    name: null,
    campaignStartDate: moment().add(2, "minutes").format("YYYY-MM-DDTHH:mm:ss"),
    campaignEndDate: moment().add(2, "minutes").format("YYYY-MM-DDTHH:mm:ss"),
    campaignLanguages: [],
    mediaTypeId: null,
    mediaUrl: null,
    campaignManagerId: null,
    mediaDetailsId: null,
    campaignStatus: true,
    campaignTypeIds: [],
    budgetAmount: 0,
    spentAmount: 0,
    budgetLevel: 0,
    budgetLevelAdminIds: [],
    expectedNumberOfLeads: 0,
    campaignBranches: [],
    unitSaleTypeId: null,
    dailyBudget: 0,
    platformCampaignId: [],
    convoloProjectId: null,
  });
  const [selected, setSelected] = useReducer(reducer, {
    campaignLanguages: [],
    mediaTypeId: null,
    campaignManagerId: null,
    mediaDetailsId: null,
    campaignTypes: [],
    budgetLevelAdmins: [],
    campaignBranches: [],
    convoloProjectSelected: null,
  });
  const [isLoading, setIsLoading] = useReducer(reducer, {
    campaignLanguages: false,
    mediaTypes: false,
    mediaDetails: false,
    users: false,
    campaignTypes: false,
    branches: false,
    saleTypes: false,
    brightCallProjectNames: false,
  });
  const [data, setData] = useReducer(reducer, {
    campaignLanguages: [],
    mediaTypes: [],
    mediaDetails: [],
    users: [],
    campaignTypes: [],
    unitSaleTypes: [],
    brightCallProjectNames: [],
  });
  const [tabIndex, setTabIndex] = useState(0);
  const [allBranches, setAllBranches] = useState([]);

  const allowedEnvironments = [
    "assets",
    "dubaiCRMConfig",
    "dubai",
    "development",
  ];

  const translationPath = "";

  const schema = Joi.object({
    name: Joi.string()
      .required()
      .messages({
        "string.empty": t(`${translationPath}campaign-name-is-required`),
      }),
    campaignStartDate: Joi.string()
      .required()
      .messages({
        "string.base": t(`${translationPath}campaign-start-date-is-required`),
        "string.empty": t(`${translationPath}campaign-start-date-is-required`),
      }),
    campaignTypeIds: Joi.array()
      .min(1)
      .required()
      .messages({
        "array.base": t(`${translationPath}campaign-type-is-required`),
        "array.empty": t(`${translationPath}campaign-type-is-required`),
        "array.min": t(`${translationPath}campaign-type-is-required`),
      }),
    campaignManagerId: Joi.number()
      .required()
      .messages({
        "number.base": t(`${translationPath}campaign-manager-is-required`),
        "number.empty": t(`${translationPath}campaign-manager-is-required`),
      }),
    budgetLevel: Joi.number()
      .custom((value, helpers) => {
        if (value > state.budgetAmount) {
          return helpers.error("budget-level-value");
        }
        return value;
      })
      .messages({
        "budget-level-value": t(
          `${translationPath}budget-level-should-be-less-than-or-equal-to-budget-amount`
        ),
      }),
    budgetAmount: Joi.number()
      .min(1)
      .required()
      .messages({
        "number.base": t(`${translationPath}budget-amount-is-required`),
        "number.empty": t(`${translationPath}budget-amount-is-required`),
        "number.min": t(`${translationPath}budget-amount-must-be-positive`),
      }),
    ...(allowedEnvironments.includes(config.name) && {
      unitSaleTypeId: Joi.number()
        .required()
        .messages({
          "number.base": t(`${translationPath}unit-sale-type-is-required`),
          "number.empty": t(`${translationPath}unit-sale-type-is-required`),
        }),
    }),
  }).options({
    abortEarly: false,
    allowUnknown: true,
  });

  const getCampaignTypes = async () => {
    setIsLoading({ id: "campaignTypes", value: true });

    const res = await lookupItemsGet({
      lookupTypeName: "CampaignType",
      pageIndex: 1,
      pageSize: 50,
    });
    if (!(res && res.status && res.status !== 200)) {
      setData({
        id: "campaignTypes",
        value: res.result,
      });
    } else {
      setData({
        id: "campaignTypes",
        value: [],
      });
    }
    setIsLoading({ id: "campaignTypes", value: false });
  };

  const getLanguages = async () => {
    setIsLoading({ id: "campaignLanguages", value: true });

    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.Languages,
    });
    if (!(res && res.status && res.status !== 200)) {
      setData({
        id: "campaignLanguages",
        value: res,
      });
    } else {
      setData({
        id: "campaignLanguages",
        value: [],
      });
    }
    setIsLoading({ id: "campaignLanguages", value: false });
  };

  const getAllmediaTypes = useCallback(async () => {
    setIsLoading({ id: "mediaTypes", value: true });
    const res = await lookupItemsGetId({ lookupTypeId: 8 });
    if (!(res && res.status && res.status !== 200)) {
      setData({ id: "mediaTypes", value: res });
    } else {
      setData({ id: "mediaTypes", value: [] });
    }
    setIsLoading({ id: "mediaTypes", value: false });
  }, []);

  const getAllBranches = useCallback(async () => {
    setIsLoading({ id: "branches", value: true });
    const res = await GetAllBranches({ pageSize: 25, pageIndex: 1 });
    if (!(res && res.status && res.status !== 200)) setAllBranches(res.result);
    else setAllBranches([]);
    setIsLoading({ id: "branches", value: false });
  }, []);

  const getAllMediaDetails = useCallback(async (mediaTypeId) => {
    setIsLoading({ id: "mediaDetails", value: true });
    const res = await lookupItemsGetId({
      lookupTypeId: 1240,
      lookupParentId: mediaTypeId,
    });
    if (!(res && res.status && res.status !== 200)) {
      setData({ id: "mediaDetails", value: res });
    } else {
      setData({ id: "mediaDetails", value: [] });
    }
    setIsLoading({ id: "mediaDetails", value: false });
  }, []);

  const getBrightCallProjectNames = async () => {
    setIsLoading({ id: "brightCallProjectNames", value: true });
    const res = await GetAllConvoloProjects();
    if (!(res && res.status && res.status !== 200)) {
      setData({
        id: "brightCallProjectNames",
        value: res.projects,
      });
    } else {
      setData({
        id: "brightCallProjectNames",
        value: [],
      });
    }
    setIsLoading({ id: "brightCallProjectNames", value: false });
  };

  const getAllUsers = useCallback(async (searchValue) => {
    setIsLoading({ id: "users", value: true });
    const res = await ApplicationUserSearch({
      pageIndex: 0,
      pageSize: 25,
      name: searchValue || "",
    });
    if (!(res && res.status && res.status !== 200)) {
      const localValue = (res && res.result) || [];
      if (localValue.length > 0) {
        setData({
          id: "users",
          value: localValue,
        });
      } else {
        setData({
          id: "users",
          value: [],
        });
      }
      setIsLoading({ id: "users", value: false });
    }
  }, []);

  const getUnitSaleTypes = async () => {
    setIsLoading({ id: "saleTypes", value: true });
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: "UnitSaleType",
      pageSize: 100,
      pageIndex: 1,
    });

    if (!(res && res.status && res.status !== 200)) {
      setData({ id: "unitSaleTypes", value: res?.result || [] });
    } else setData({ id: "unitSaleTypes", value: [] });
    setIsLoading({ id: "saleTypes", value: false });
  };

  const saveHandler = async (event) => {
    event.preventDefault();

    const { error } = schema.validate(state, { abortEarly: false });
    if (error) {
      showError(error.message);
      return;
    }

    setIsSubmitLoading(true);

    const developerContactId = developerId || undefined;
    const body = { ...state, developerContactId };

    try {
      let res;
      if (activeItem) {
        res = await UpdatePropertyCampaign({
          campaignId: activeItem.propertyCampaignId,
          body,
        });
      } else {
        res = await CreatePropertyCampaign(body);
      }

      const errorMessage = res?.Message || res?.data?.Message;
      if (errorMessage) {
        if (errorMessage.includes("Convolo")) {
          const displayMsg = errorMessage.split(":").slice(1).join(":").trim();
          showError(displayMsg);
        } else {
          showError(errorMessage);
        }
        return;
      }

      if (!(res && res.status && res.status !== 200)) {
        showSuccess(
          t(
            activeItem
              ? "campaign-updated-successfully"
              : "campaign-created-successfully"
          )
        );
        if (onSave) onSave();
      } else {
        showError(
          t(activeItem ? "campaign-updated-failed" : "campaign-created-failed")
        );
      }
    } catch (err) {
      const backendError = err?.Message || err?.response?.data?.Message;
      if (backendError?.includes("Convolo")) {
        const displayMsg = backendError.split(":").slice(1).join(":").trim();
        showError(displayMsg);
      } else {
        showError(t("something-went-wrong"));
      }
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleTabClick = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleMediaDetailsValue = (mediaTypeId) => {
    getAllmediaTypes();
    setSelected({ id: "mediaDetailsId", value: null });
    setState({ id: "mediaDetailsId", value: null });

    if (mediaTypeId) getAllMediaDetails(mediaTypeId);
    else getAllMediaDetails();
  };

  const handleMediaTypeValue = (mediaDetailsItem) => {
    if (mediaDetailsItem) {
      const mediaTypeItem = {
        lookupItemId: mediaDetailsItem.parentLookupItemId,
        lookupItemName: mediaDetailsItem.parentLookupItemName,
      };
      setSelected({ id: "mediaTypeId", value: mediaTypeItem });
      setState({ id: "mediaTypeId", value: mediaTypeItem.lookupItemId });

      setData({ id: "mediaTypes", value: [mediaTypeItem] });
      getAllMediaDetails(mediaTypeItem.lookupItemId);
    } else {
      getAllmediaTypes();
    }
  };

  useEffect(() => {
    getLanguages();
    getCampaignTypes();
    getBrightCallProjectNames();
    getAllUsers();
    getAllBranches();
    getUnitSaleTypes();
  }, []);

  useEffect(() => {
    if (activeItem && activeItem.mediaTypeId && activeItem.mediaDetailsId) {
      getAllMediaDetails(activeItem.mediaTypeId);

      const mediaTypeItem = {
        lookupItemId: activeItem.mediaTypeId,
        lookupItemName: activeItem.mediaTypeName,
      };
      setSelected({ id: "mediaTypeId", value: mediaTypeItem });
      setState({ id: "mediaTypeId", value: mediaTypeItem.lookupItemId });
      setData({ id: "mediaTypes", value: [mediaTypeItem] });

      const mediaDetailsItem = {
        lookupItemId: activeItem.mediaDetailsId,
        lookupItemName: activeItem.mediaDetailsName,
      };
      setSelected({ id: "mediaDetailsId", value: mediaDetailsItem });
      setState({ id: "mediaDetailsId", value: mediaDetailsItem.lookupItemId });
    } else if (activeItem && activeItem.mediaTypeId) {
      getAllMediaDetails(activeItem.mediaTypeId);

      const mediaTypeItem = {
        lookupItemId: activeItem.mediaTypeId,
        lookupItemName: activeItem.mediaTypeName,
      };
      setSelected({ id: "mediaTypeId", value: mediaTypeItem });
      setState({ id: "mediaTypeId", value: mediaTypeItem.lookupItemId });
      setData({ id: "mediaTypes", value: [mediaTypeItem] });
    } else {
      getAllmediaTypes();
      getAllMediaDetails();
    }
  }, [activeItem]);

  useEffect(() => {
    if (activeItem) {
      const stateCampaignLanguageIds =
        activeItem.propertyCampaignLanguages &&
        activeItem.propertyCampaignLanguages.map((item) => item.languageId);

      const stateBudgetLevelAdminsIds =
        activeItem.campaignBudgetLevelAdminDtos &&
        activeItem.campaignBudgetLevelAdminDtos.map((item) => item.userId);

      const stateCampaignTypeIds =
        activeItem.campaignTypeChoicesDtos &&
        activeItem.campaignTypeChoicesDtos.map((item) => item.campaignTypeId);

      const statePropertyCampaignBranchesIds =
        activeItem.propertyCampaignBranches &&
        activeItem.propertyCampaignBranches.map((item) => item.branchId);

      setState({
        id: "edit",
        value: {
          propertyId: activeItem.propertyId || undefined,
          name: activeItem.name,
          campaignStartDate: activeItem.campaignStartDate,
          campaignEndDate: activeItem.campaignEndDate,
          campaignLanguages: stateCampaignLanguageIds || [],
          mediaTypeId: activeItem.mediaTypeId || null,
          mediaUrl: activeItem.mediaUrl,
          campaignManagerId: activeItem.campaignManagerId,
          mediaDetailsId: activeItem.mediaDetailsId,
          campaignStatus: activeItem.campaignStatus,
          campaignTypeIds: stateCampaignTypeIds || [],
          campaignBranches: statePropertyCampaignBranchesIds || [],
          budgetAmount: activeItem.budgetAmount || 0,
          spentAmount: activeItem.spentAmount || 0,
          budgetLevel: activeItem.budgetLevel || 0,
          budgetLevelAdminIds: stateBudgetLevelAdminsIds || [],
          expectedNumberOfLeads: activeItem.expectedNumberOfLeads || 0,
          actualNumberOfLeads: activeItem.actualNumberOfLeads || 0,
          unitSaleTypeId: activeItem.unitSaleTypeId,
          dailyBudget: activeItem.dailyBudget || 0,
          platformCampaignId: activeItem.platformCampaignId || [],
        },
      });
    }
  }, [activeItem]);

  useEffect(() => {
    if (
      activeItem?.convoloProjectId &&
      Array.isArray(data.brightCallProjectNames)
    ) {
      const selectedProject = data.brightCallProjectNames.find(
        (project) => project.id === String(activeItem.convoloProjectId)
      );
      setSelected({
        id: "convoloProjectSelected",
        value: selectedProject || null,
      });
    }
  }, [activeItem, data.brightCallProjectNames]);

  useEffect(() => {
    if (activeItem) {
      const selectedCampaignLanguages =
        activeItem.propertyCampaignLanguages.map((item) => ({
          lookupItemId: item.languageId,
          lookupItemName: item.languageName,
        }));
      setSelected({
        id: "campaignLanguages",
        value: selectedCampaignLanguages,
      });

      const selectedBudgetLevelAdmins =
        activeItem.campaignBudgetLevelAdminDtos.map((item) => ({
          applicationUserId: item.userId,
          fullName: item.userName,
        }));
      setSelected({
        id: "budgetLevelAdmins",
        value: selectedBudgetLevelAdmins,
      });

      const selectedCampaignTypes = activeItem.campaignTypeChoicesDtos.map(
        (item) => ({
          lookupItemId: item.campaignTypeId,
          lookupItemName: item.campaignTypeName,
        })
      );
      setSelected({ id: "campaignTypes", value: selectedCampaignTypes });

      setSelected({
        id: "campaignBranches",
        value: activeItem.propertyCampaignBranches,
      });

      if (!selected.campaignManagerId && activeItem.campaignManagerId) {
        const campaignManagerItem = {
          applicationUserId: activeItem.campaignManagerId,
          fullName: activeItem.campaignManagerName,
        };
        setSelected({ id: "campaignManagerId", value: campaignManagerItem });
      }
    }
  }, [activeItem, data]);

  return (
    <div>
      <Dialog
        open={isDialogOpen}
        onClose={onClose}
        className="activities-management-dialog-wrapper"
      >
        <Spinner isActive={isSubmitLoading} isAbsolute />
        <form noValidate onSubmit={saveHandler}>
          <DialogTitle id="alert-dialog-slide-title">
            {t(`${(activeItem && "edit-campaign") || "add-new-campaign"}`)}
          </DialogTitle>
          <DialogContent>
            <>
              <Tabs
                value={tabIndex}
                onChange={handleTabClick}
                aria-label="icon label tabs"
                textColor="primary"
                classes={{
                  indicator: classes.indicator,
                  scroller: classes.scroller,
                  fixed: classes.fixed,
                }}
              >
                <Tab
                  classes={{
                    root: classes.root,
                    wrapper: classes.wrapper,
                    labelIcon: classes.labelIcon,
                  }}
                  icon={<span className="mr-2 mdi mdi-format-list-checks " />}
                  label={t(`Details`)}
                />
                <Tab
                  classes={{
                    root: classes.root,
                    wrapper: classes.wrapper,
                    labelIcon: classes.labelIcon,
                  }}
                  icon={<span className="mr-2 mdi mdi-currency-usd" />}
                  label={t(`Budget&Leads`)}
                />
              </Tabs>
              {tabIndex === 0 && (
                <div className="d-flex">
                  <div className="w-50 mx-2">
                    <div className="my-2">
                      <Inputs
                        idRef="campaignNameRef"
                        labelValue={t(`campaign-name`)}
                        labelClasses="Requierd-Color"
                        value={state.name}
                        inputPlaceholder={t(`Add-text`)}
                        onInputChanged={(event) => {
                          const { value } = event.target;
                          setState({
                            id: "name",
                            value: value,
                          });
                        }}
                        parentTranslationPath={parentTranslationPath}
                        helperText={getErrorByName(schema, "name").message}
                        error={getErrorByName(schema, "name").error}
                        isWithError
                        isSubmitted={isSubmitLoading}
                      />
                    </div>
                    <div className="my-2">
                      <AutocompleteComponent
                        idRef="mediaDetailsRef"
                        labelValue={t(`media-details`)}
                        selectedValues={selected.mediaDetailsId || []}
                        isDisabled={isLoading.mediaDetails}
                        data={data.mediaDetails || []}
                        displayLabel={(option) => option.lookupItemName || ""}
                        multiple={false}
                        withoutSearchButton
                        onChange={(event, newValue) => {
                          setState({
                            id: "mediaDetailsId",
                            value: newValue && newValue.lookupItemId,
                          });
                          setSelected({
                            id: "mediaDetailsId",
                            value: newValue,
                          });
                          handleMediaTypeValue(newValue);
                        }}
                        isLoading={isLoading.mediaDetails}
                      />
                    </div>
                    <div className="my-3">
                      <DatePickerComponent
                        idRef="campaignStartDateRef"
                        labelValue={t(`start-date`)}
                        labelClasses="Requierd-Color"
                        placeholder="DD/MM/YYYY"
                        format="YYYY/MM/DD"
                        parentTranslationPath={parentTranslationPath}
                        value={
                          state.campaignStartDate
                            ? state.campaignStartDate
                            : moment().format("YYYY-MM-DDTHH:mm:ss")
                        }
                        minDate={moment().format("YYYY-MM-DDTHH:mm:ss")}
                        onDateChanged={(newValue) => {
                          setState({
                            id: "campaignStartDate",
                            value:
                              (newValue &&
                                moment(newValue).format(
                                  "YYYY-MM-DDTHH:mm:ss"
                                )) ||
                              null,
                          });

                          const isEndDateBeforeStartDate = moment(
                            state.campaignEndDate
                          ).isBefore(newValue);
                          if (isEndDateBeforeStartDate) {
                            setState({
                              id: "campaignEndDate",
                              value:
                                (newValue &&
                                  moment(newValue).format(
                                    "YYYY-MM-DDTHH:mm:ss"
                                  )) ||
                                null,
                            });
                          }
                        }}
                        helperText={
                          getErrorByName(schema, "campaignStartDate").message
                        }
                        error={
                          getErrorByName(schema, "campaignStartDate").error
                        }
                        isWithError
                        isSubmitted={isSubmitLoading}
                      />
                    </div>
                    <div className="my-1">
                      <AutocompleteComponent
                        idRef="campaignLanguagesRef"
                        labelValue={t(`campaign-languages`)}
                        isDisabled={isLoading.campaignLanguages}
                        selectedValues={selected.campaignLanguages || []}
                        data={data.campaignLanguages || []}
                        chipsLabel={(option) => option.lookupItemName || ""}
                        displayLabel={(option) => option.lookupItemName || ""}
                        multiple={true}
                        withoutSearchButton
                        isWithError
                        onChange={(event, newValue) => {
                          const languageLookupIds =
                            newValue &&
                            newValue.map((item) => item.lookupItemId);
                          setState({
                            id: "campaignLanguages",
                            value: languageLookupIds || [],
                          });
                          setSelected({
                            id: "campaignLanguages",
                            value: newValue,
                          });
                        }}
                        isLoading={isLoading.campaignLanguages}
                      />
                    </div>
                    <div className="my-3">
                      <SelectComponet
                        idRef={`CampaignStatusRef`}
                        data={[
                          {
                            value: true,
                            text: "Active",
                          },
                          {
                            value: false,
                            text: "In-active",
                          },
                        ]}
                        value={state.campaignStatus}
                        labelValue="campaign-status"
                        valueInput="value"
                        textInput="text"
                        onSelectChanged={(value) => {
                          setState({
                            id: "campaignStatus",
                            value: value,
                          });
                        }}
                        emptyItem={{
                          value: null,
                          text: "",
                        }}
                        translationPathForData={parentTranslationPath}
                        parentTranslationPath={parentTranslationPath}
                      />
                    </div>
                    <div className="my-3">
                      <AutocompleteComponent
                        idRef="branchRef"
                        multiple
                        isDisabled={isLoading.branches}
                        isLoading={isLoading.branches}
                        labelValue={t(`branchs`)}
                        data={allBranches || []}
                        selectedValues={selected.campaignBranches || []}
                        chipsLabel={(option) =>
                          (option && option.branchName) || ""
                        }
                        displayLabel={(option) =>
                          (option && option.branchName) || ""
                        }
                        withoutSearchButton
                        parentTranslationPath={parentTranslationPath}
                        onChange={(event, newValue) => {
                          const languageLookupIds =
                            newValue && newValue.map((item) => item.branchId);
                          setState({
                            id: "campaignBranches",
                            value: languageLookupIds || [],
                          });
                          setSelected({
                            id: "campaignBranches",
                            value: newValue,
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="w-50 mx-2">
                    <div className="my-2">
                      <AutocompleteComponent
                        idRef="mediaTypesRef"
                        labelValue={t(`media`)}
                        isDisabled={isLoading.mediaTypes}
                        selectedValues={selected.mediaTypeId || []}
                        data={data.mediaTypes || []}
                        chipsLabel={(option) => option.lookupItemName || ""}
                        displayLabel={(option) => option.lookupItemName || ""}
                        multiple={false}
                        withoutSearchButton
                        onChange={(event, newValue) => {
                          setState({
                            id: "mediaTypeId",
                            value: newValue && newValue.lookupItemId,
                          });
                          setSelected({
                            id: "mediaTypeId",
                            value: newValue,
                          });
                          handleMediaDetailsValue(
                            newValue && newValue.lookupItemId
                          );
                        }}
                        isLoading={isLoading.mediaTypes}
                      />
                    </div>
                    <div className="my-2">
                      <AutocompleteComponent
                        idRef="CampaignManagerRef"
                        labelValue="campaign-manager"
                        selectedValues={selected.campaignManagerId}
                        isDisabled={isLoading.users}
                        labelClasses="Requierd-Color"
                        multiple={false}
                        data={data.users || []}
                        displayLabel={(option) => option.fullName || ""}
                        renderOption={(option) =>
                          ((option.userName || option.fullName) &&
                            `${option.fullName} (${option.userName})`) ||
                          ""
                        }
                        getOptionSelected={(option) =>
                          option.applicationUserId === state.campaignManagerId
                        }
                        withoutSearchButton
                        isLoading={isLoading.users}
                        onKeyDown={() => {
                          setSelected({ id: "campaignManagerId", value: null });
                        }}
                        onInputKeyUp={(e) => {
                          const searchText = e?.target?.value;
                          if (searchTimer.current)
                            clearTimeout(searchTimer.current);
                          searchTimer.current = setTimeout(() => {
                            getAllUsers(searchText);
                          }, 700);
                        }}
                        isWithError
                        parentTranslationPath={parentTranslationPath}
                        onChange={(event, newValue) => {
                          setSelected({
                            id: "campaignManagerId",
                            value: newValue,
                          });
                          setState({
                            id: "campaignManagerId",
                            value:
                              (newValue && newValue.applicationUserId) || null,
                          });
                        }}
                        helperText={
                          getErrorByName(schema, "campaignManagerId").message
                        }
                        error={
                          getErrorByName(schema, "campaignManagerId").error
                        }
                        isSubmitted={isSubmitLoading}
                      />
                    </div>
                    <div className="my-2">
                      <DatePickerComponent
                        idRef="campaignEndDateRef"
                        labelValue={t(`end-date`)}
                        placeholder="DD/MM/YYYY"
                        format="YYYY/MM/DD"
                        labelClasses="mt-3"
                        parentTranslationPath={parentTranslationPath}
                        value={state.campaignEndDate}
                        minDate={state.campaignStartDate || ""}
                        onDateChanged={(newValue) => {
                          setState({
                            id: "campaignEndDate",
                            value:
                              (newValue &&
                                moment(newValue).format(
                                  "YYYY-MM-DDTHH:mm:ss"
                                )) ||
                              null,
                          });
                        }}
                      />
                    </div>
                    <div className="mt-3">
                      <Inputs
                        idRef="mediaLinkRef"
                        labelValue={t(`media-link`)}
                        inputPlaceholder={`Add-text`}
                        value={state.mediaUrl}
                        onInputChanged={(event) => {
                          const { value } = event.target;
                          setState({
                            id: "mediaUrl",
                            value: value,
                          });
                        }}
                        parentTranslationPath={parentTranslationPath}
                      />
                    </div>
                    <div className="mt-2">
                      <AutocompleteComponent
                        idRef="campaignTypeRef"
                        labelValue={t(`campaign-type`)}
                        isDisabled={isLoading.campaignTypes}
                        labelClasses="Requierd-Color"
                        selectedValues={selected.campaignTypes || []}
                        data={data.campaignTypes || []}
                        chipsLabel={(option) => option.lookupItemName || ""}
                        displayLabel={(option) => option.lookupItemName || ""}
                        multiple={true}
                        withoutSearchButton
                        isWithError
                        onChange={(event, newValue) => {
                          const languageLookupIds =
                            newValue &&
                            newValue.map((item) => item.lookupItemId);
                          setState({
                            id: "campaignTypeIds",
                            value: languageLookupIds || [],
                          });
                          setSelected({
                            id: "campaignTypes",
                            value: newValue,
                          });
                        }}
                        isLoading={isLoading.campaignTypes}
                        helperText={
                          getErrorByName(schema, "campaignTypeIds").message
                        }
                        error={getErrorByName(schema, "campaignTypeIds").error}
                        isSubmitted={isSubmitLoading}
                      />
                    </div>
                    <div className="mt-2">
                      <SelectComponet
                        idRef="unitSaleTypeRef"
                        isLoading={isLoading.saleTypes}
                        labelValue={t(`unit-sale-types`)}
                        data={data.unitSaleTypes}
                        valueInput="lookupItemId"
                        textInput="lookupItemName"
                        emptyItem={{
                          value: null,
                          text: "select-unit-sale-types",
                          isDisabled: false,
                        }}
                        value={state.unitSaleTypeId}
                        onSelectChanged={(value) =>
                          setState({ id: "unitSaleTypeId", value })
                        }
                        parentTranslationPath={parentTranslationPath}
                        helperText={
                          getErrorByName(schema, "unitSaleTypeId").message
                        }
                        error={getErrorByName(schema, "unitSaleTypeId").error}
                        isWithError
                        isSubmitted={isSubmitLoading}
                        labelClasses={
                          allowedEnvironments.includes(config.name)
                            ? "Requierd-Color"
                            : ""
                        }
                      />
                    </div>
                    <div className="mt-2">
                      <AutocompleteComponent
                        idRef="BrightCallProjectNameRef"
                        labelValue={t(`bright-call-project-name`)}
                        selectedValues={selected.convoloProjectSelected}
                        isDisabled={isLoading.brightCallProjectNames}
                        multiple={false}
                        data={data.brightCallProjectNames || []}
                        displayLabel={(option) => option.name || ""}
                        renderOption={(option) => option.name || ""}
                        getOptionSelected={(option) =>
                          option.name === state.convoloProjectId
                        }
                        withoutSearchButton
                        isLoading={isLoading.brightCallProjectNames}
                        onKeyDown={() => {
                          setSelected({
                            id: "convoloProjectSelected",
                            value: null,
                          });
                        }}
                        onInputKeyUp={(e) => {
                          const searchText = e?.target?.value;
                          if (searchTimer.current)
                            clearTimeout(searchTimer.current);
                          searchTimer.current = setTimeout(() => {
                            getAllUsers(searchText);
                          }, 700);
                        }}
                        isWithError
                        parentTranslationPath={parentTranslationPath}
                        translationPath={translationPath}
                        onChange={(event, newValue) => {
                          setSelected({
                            id: "convoloProjectSelected",
                            value: newValue,
                          });
                          setState({
                            id: "convoloProjectId",
                            value: newValue.id || null,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {tabIndex === 1 && (
                <div className="d-flex">
                  <div className="w-50 mx-2">
                    <div className="my-2">
                      <Inputs
                        idRef="budgetAmountRef"
                        labelValue={t(`budget-amount`)}
                        value={state.budgetAmount}
                        labelClasses="Requierd-Color"
                        type="number"
                        onInputChanged={(event) => {
                          const { value } = event.target;
                          const isNumeralValue = !isNaN(+value);
                          if (isNumeralValue) {
                            setState({
                              id: "budgetAmount",
                              value: +value,
                            });
                          } else {
                            setState({
                              id: "budgetAmount",
                              value: 0,
                            });
                          }
                        }}
                        helperText={
                          getErrorByName(schema, "budgetAmount").message
                        }
                        error={getErrorByName(schema, "budgetAmount").error}
                        isWithError
                        isSubmitted={isSubmitLoading}
                      />
                    </div>
                    <div className="my-2">
                      <Inputs
                        idRef="dailyBudgetRef"
                        labelValue={t(`daily-budget`)}
                        value={state.dailyBudget}
                        type="number"
                        min={0}
                        //withNumberFormat
                        onInputChanged={(event) => {
                          const { value } = event.target;
                          const isNumeralValue = !isNaN(+value);
                          if (isNumeralValue) {
                            setState({
                              id: "dailyBudget",
                              value: value < 0 ? value * -1 : +value,
                            });
                          } else {
                            setState({
                              id: "dailyBudget",
                              value: 0,
                            });
                          }
                        }}
                        endAdornment={
                          <InputAdornment position="end" className="px-2">
                            AED
                          </InputAdornment>
                        }
                      />
                    </div>
                    <div className="my-2">
                      <Inputs
                        idRef="budgetLevelRef"
                        labelValue={t(`budget-level`)}
                        value={state.budgetLevel}
                        type="number"
                        onInputChanged={(event) => {
                          const { value } = event.target;
                          const isNumeralValue = !isNaN(+value);
                          if (+value <= state.budgetAmount) {
                            if (isNumeralValue) {
                              setState({
                                id: "budgetLevel",
                                value: +value,
                              });
                            } else {
                              setState({
                                id: "budgetLevel",
                                value: 0,
                              });
                            }
                          }
                        }}
                        helperText={
                          getErrorByName(schema, "budgetLevel").message
                        }
                        error={getErrorByName(schema, "budgetLevel").error}
                        isWithError
                        isSubmitted={isSubmitLoading}
                      />
                    </div>
                    <div className="my-2">
                      <Inputs
                        idRef="expectedNumberOfLeadsRef"
                        labelValue={t(`Expected-No-of-Leads`)}
                        value={state.expectedNumberOfLeads}
                        type="number"
                        onInputChanged={(event) => {
                          const { value } = event.target;
                          const isNumeralValue = !isNaN(+value);
                          const isIntegerValue = Number.isInteger(+value);
                          if (isNumeralValue && isIntegerValue) {
                            setState({
                              id: "expectedNumberOfLeads",
                              value: +value,
                            });
                          } else {
                            setState({
                              id: "expectedNumberOfLeads",
                              value: 0,
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="w-50 mx-2">
                    <div className="my-2">
                      <Inputs
                        idRef="spentAmountRef"
                        labelValue={t(`spent-amount`)}
                        value={state.spentAmount}
                        type="number"
                        onInputChanged={(event) => {
                          const { value } = event.target;
                          const isNumeralValue = !isNaN(+value);
                          if (isNumeralValue) {
                            setState({
                              id: "spentAmount",
                              value: +value,
                            });
                          } else {
                            setState({
                              id: "spentAmount",
                              value: 0,
                            });
                          }
                        }}
                        isDisabled={
                          activeItem && activeItem.campaignStatus == false
                        }
                      />
                    </div>
                    <div className="my-2">
                      <AutocompleteComponent
                        idRef="budgetLevelAdminsRef"
                        labelValue="budget-level-admins"
                        selectedValues={selected.budgetLevelAdmins || []}
                        isDisabled={isLoading.users}
                        multiple={true}
                        data={data.users || []}
                        chipsLabel={(option) => option.fullName || ""}
                        displayLabel={(option) => option.fullName || ""}
                        renderOption={(option) =>
                          ((option.userName || option.fullName) &&
                            `${option.fullName} (${option.userName})`) ||
                          ""
                        }
                        withoutSearchButton
                        isLoading={isLoading.users}
                        onKeyDown={() => {
                          setSelected({ id: "budgetLevelAdmins", value: null });
                        }}
                        onInputKeyUp={(e) => {
                          const searchText = e?.target?.value;
                          if (searchTimer.current)
                            clearTimeout(searchTimer.current);
                          searchTimer.current = setTimeout(() => {
                            getAllUsers(searchText);
                          }, 700);
                        }}
                        isWithError
                        parentTranslationPath={parentTranslationPath}
                        onChange={(event, newValue) => {
                          const budgetLevelAdminsIds =
                            newValue &&
                            newValue.map((item) => item.applicationUserId);

                          setSelected({
                            id: "budgetLevelAdmins",
                            value: newValue,
                          });
                          setState({
                            id: "budgetLevelAdminIds",
                            value: budgetLevelAdminsIds || null,
                          });
                        }}
                      />
                    </div>
                    {activeItem && (
                      <div className="my-3">
                        <Inputs
                          idRef="actualNumberOfLeadsRef"
                          labelValue={t(`actual-number-Of-leads`)}
                          value={state.actualNumberOfLeads}
                          isDisabled
                        />
                      </div>
                    )}
                    <div className="my-2">
                      <MultiValueTextInput
                        idRef="platformCampaignIdRef"
                        label={t(`platform-campaign-id`)}
                        type="number"
                        value={state.platformCampaignId}
                        onChange={(newValues) => {
                          setState({
                            id: "platformCampaignId",
                            value: newValues,
                          });
                        }}
                        isSubmitted={isSubmitLoading}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          </DialogContent>
          <DialogActions>
            <ButtonBase
              onClick={onClose}
              className="btns theme-solid bg-cancel"
            >
              {t(`cancel`)}
            </ButtonBase>
            <ButtonBase
              className="btns theme-solid"
              onClick={saveHandler}
              type="submit"
            >
              {t(`save`)}
            </ButtonBase>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};
